import { PlusOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Segmented, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useInvestigationsServiceGetInvestigationTestsEndpoint,
  useInvestigationsServiceGetListSpecimenInvestigationsEndpoint,
  useInvestigationsServiceGetListSpecimensEndpoint,
  useInvestigationsServiceGetListSpecimensEndpointKey,
  usePersonsServiceGetPersonByIdEndpoint
} from 'src/api/queries'
import {
  Countries,
  IdentifierWithTranslationDto,
  InvestigationTestResultDto,
  Permissions,
  SpecimenItemDto
} from 'src/api/requests'
import { Paragraph, TbTag, Text } from 'src/components'
import {
  isExternalResult,
  ResultWithApproval
} from 'src/features/persons/utils'
import { TestStatusTag } from 'src/features/tests/components'
import { getFullName } from 'src/features/users/utils'
import { useTablePagination, useUser } from 'src/hooks'
import {
  dayMonthYearHourMinutesFormatWithDot,
  formatUtcDateTime
} from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

import {
  ExternalResultModal,
  InvestigationDemandModalScreening,
  ResultActions
} from '../components'
import { InvestigationDemandModal } from '../components/PersonConsultations/InvestigationDemandModal'

import { useStyles } from './styles'

export const PersonsScreeningPage = () => {
  const { t, currentLang } = useLocales()
  const { styles } = useStyles()
  const { id } = useParams()
  const navigate = useNavigate()
  const { hasPermission } = useUser()
  const [isOpen, setIsOpen] = useState(false)
  const [isTbOpen, setIsTbOpen] = useState(false)
  const [isExternalOpen, setIsExternalOpen] = useState(false)
  const [tbFilter, setTbFilter] = useState<'all' | 'tb' | 'nonTb'>('all')
  // const [isPreventiveTreatmentOpen, setIsPreventiveTreatmentOpen] =
  //   useState(false)

  const goToResults = (specimenId: string) =>
    navigate(
      `${paths.persons.details(String(id))}${paths.persons.investigationResults.root()}?specimenId=${specimenId}&from=screening`
    )

  const pagination = useTablePagination()

  const queryClient = useQueryClient()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    personIds: [String(id)],
    // Only the specimens of a screening; the Investigații tab lists all of them.
    isScreening: true
  }

  // const riskQueryParams = {
  //   pageNumber: pagination.current,
  //   pageSize: pagination.pageSize,
  //   personId: String(id)
  // }

  const { data, isLoading } =
    useInvestigationsServiceGetListSpecimensEndpoint(queryParams)

  const pageSpecimenIds = useMemo(
    () =>
      (data?.data ?? [])
        .map((specimen) => specimen.id)
        .filter((id): id is string => !!id),
    [data?.data]
  )

  // The specimens list doesn't carry the investigation type, so we fetch the
  // investigations for the specimens on this page and group their types back
  // onto each specimen row by specimen code.
  const { data: specimenInvestigations } =
    useInvestigationsServiceGetListSpecimenInvestigationsEndpoint(
      { institutionIds: [], specimenIds: pageSpecimenIds, pageSize: 100 },
      [JSON.stringify(pageSpecimenIds)],
      { enabled: pageSpecimenIds.length > 0 }
    )

  const investigationTypesByCode = useMemo(() => {
    const map = new Map<string, IdentifierWithTranslationDto[]>()

    for (const investigation of specimenInvestigations?.data ?? []) {
      if (!investigation.specimenCode || !investigation.type) continue

      const types = map.get(investigation.specimenCode) ?? []

      if (!types.some((type) => type.id === investigation.type?.id)) {
        types.push(investigation.type)
      }

      map.set(investigation.specimenCode, types)
    }

    return map
  }, [specimenInvestigations?.data])

  // Results live on investigation tests, not on the specimen list. Fetch the
  // tests for the specimens on this page and group their results back onto each
  // specimen row via investigationId -> specimenCode (from specimenInvestigations).
  const { data: pageTests } =
    useInvestigationsServiceGetInvestigationTestsEndpoint(
      { specimenIds: pageSpecimenIds, pageSize: 100 },
      [JSON.stringify(pageSpecimenIds)],
      { enabled: pageSpecimenIds.length > 0 }
    )

  const resultsBySpecimenCode = useMemo(() => {
    const specimenCodeByInvestigationId = new Map<string, string>()
    const typeByInvestigationId = new Map<
      string,
      IdentifierWithTranslationDto
    >()
    for (const investigation of specimenInvestigations?.data ?? []) {
      if (investigation.id && investigation.specimenCode) {
        specimenCodeByInvestigationId.set(
          investigation.id,
          investigation.specimenCode
        )
      }
      if (investigation.id && investigation.type) {
        typeByInvestigationId.set(investigation.id, investigation.type)
      }
    }

    const map = new Map<string, ResultWithApproval[]>()
    for (const test of pageTests?.data ?? []) {
      const specimenCode = test.investigationId
        ? specimenCodeByInvestigationId.get(test.investigationId)
        : undefined

      if (!specimenCode || !test.results?.length) continue

      const results = map.get(specimenCode) ?? []
      results.push(
        ...test.results.map((result) => ({
          result,
          isApproved: test.isApproved,
          referralDoctorUser: test.referralDoctorUser,
          referralDoctorName: test.referralDoctorName,
          typeId: test.investigationId
            ? typeByInvestigationId.get(test.investigationId)?.id
            : undefined
        }))
      )
      map.set(specimenCode, results)
    }

    return map
  }, [pageTests?.data, specimenInvestigations?.data])

  const { data: person } = usePersonsServiceGetPersonByIdEndpoint(
    { id: String(id) },
    [String(id)],
    { enabled: !!id }
  )

  const isForeignPerson =
    !!person?.citizenship && person.citizenship !== Countries.MD

  const canAddForeignInvestigation =
    isForeignPerson &&
    hasPermission(Permissions.INVESTIGATIONS_INVESTIGATIONS_CREATE_FOREIGN)

  // const { data: riskGroups } =
  //   usePersonRiskGroupServiceGetPersonRiskGroupsEndpoint(riskQueryParams)

  // const activeRiskGroup = riskGroups?.data?.find((item) => !item.endDate)

  const isScreening = !hasPermission([
    Permissions.PERSONS_PATIENT_VIEW_OWN,
    Permissions.PERSONS_PATIENT_VIEW_INSTITUTION
  ])

  const columns: ColumnsType<SpecimenItemDto> = [
    {
      key: 'investigation number',
      title: t('investigations.investigation_number'),
      render: (_, { code, isTb }) => (
        <Flex gap={8} align="center">
          {code}
          <TbTag isTb={isTb} />
        </Flex>
      )
    },
    {
      key: 'created date',
      title: t('investigations.performed_date'),
      render: ({ createdAt }) => formatUtcDateTime(createdAt)
    },
    {
      key: 'harvest date',
      title: t('investigations.harvest_date'),
      render: ({ harvestDate }) =>
        harvestDate
          ? dayjs
              .utc(harvestDate)
              .tz()
              .format(dayMonthYearHourMinutesFormatWithDot)
          : '-'
    },
    {
      key: 'specimen type',
      title: t('consultations.specimen_type'),
      render: (_, { type }) => type?.translation?.[currentLang.value] ?? '-'
    },
    {
      key: 'investigation type',
      title: t('classifiers.category'),
      render: (_, { code }) => {
        const types = code ? (investigationTypesByCode.get(code) ?? []) : []

        if (!types.length) return '-'

        return (
          <div>
            {types.map((item) => (
              <div key={item.id}>{item.translation?.[currentLang.value]}</div>
            ))}
          </div>
        )
      }
    },
    {
      key: 'status',
      title: t('form.status'),
      render: (_, { status }) => <TestStatusTag status={status} />
    },
    {
      key: 'test type',
      title: t('tests_results.type'),
      render: (_, { demand }) => {
        const text = (
          <div>
            {demand?.categories?.map((item) => (
              <div key={item.id}>{item.translation?.[currentLang.value]}</div>
            ))}
          </div>
        )

        return (
          <Paragraph
            ellipsis={{
              rows: 2,
              tooltip: text
            }}
          >
            {text}
          </Paragraph>
        )
      }
    },
    {
      key: 'results',
      title: t('analysis.result'),
      render: (_, { code, id: specimenId }) => {
        const results = code ? (resultsBySpecimenCode.get(code) ?? []) : []
        if (!results.length) return '-'

        // Prefer the device/laboratory status; fall back to the manual result
        // option / value when it is empty.
        const formatValue = (result: InvestigationTestResultDto) =>
          result.laboratoryResultStatus ||
          [
            result.resultOption?.translation?.[currentLang.value],
            result.resultValueOption?.translation?.[currentLang.value],
            result.resultValue
          ]
            .filter(
              (value) => value !== null && value !== undefined && value !== ''
            )
            .join(', ') ||
          '-'

        return (
          <Flex vertical gap={2}>
            {results.map(
              (
                {
                  result,
                  isApproved,
                  referralDoctorUser,
                  referralDoctorName,
                  typeId
                },
                index
              ) => (
                <Flex
                  key={`${result.testType?.id ?? 'result'}-${index}`}
                  gap={4}
                  align="center"
                  wrap="wrap"
                >
                  <Text>
                    {result.testType?.translation?.[currentLang.value] ?? '-'}:{' '}
                    {formatValue(result)}
                  </Text>
                  {isExternalResult(result) && (
                    <Tag color="orange">
                      {t('persons.external_result.badge')}
                    </Tag>
                  )}
                  <ResultActions
                    result={result}
                    isApproved={isApproved}
                    specimenId={specimenId}
                    referralDoctorUserId={referralDoctorUser?.id}
                    referralDoctorName={referralDoctorName}
                    referralDoctorUserName={getFullName(
                      referralDoctorUser?.firstName,
                      referralDoctorUser?.lastName
                    )}
                    typeId={typeId}
                  />
                </Flex>
              )
            )}
          </Flex>
        )
      }
    },
    {
      key: 'test_results',
      title: t('investigations.tests'),
      render: (_, { id: specimenId }) => (
        <Button
          type="link"
          onClick={() => specimenId && goToResults(specimenId)}
        >
          {t('common.details')}
        </Button>
      )
    }
    // ...(activeRiskGroup?.riskGroup?.recommendedPreventiveTreatment
    //   ? [
    //     {
    //       key: 'actions',
    //       fixed: 'right' as const,
    //       render: () => (
    //         <Button
    //           icon={<EditOutlined />}
    //           onClick={onTogglePreventiveTreatment}
    //         >
    //           {t('screening.preventive_treatment')}
    //         </Button>
    //       )
    //     }
    //   ]
    //   : [])
  ]

  const onSuccessCreate = () => {
    queryClient.invalidateQueries({
      queryKey: [useInvestigationsServiceGetListSpecimensEndpointKey]
    })
  }

  const onToggle = () => setIsOpen((prev) => !prev)
  const onToggleTb = () => setIsTbOpen((prev) => !prev)
  const onToggleExternal = () => setIsExternalOpen((prev) => !prev)

  // The list endpoint has no isTb filter, so the TB / non-TB toggle is applied
  // client-side to the current page.
  const filteredSpecimens = (data?.data ?? []).filter((specimen) => {
    if (tbFilter === 'tb') return !!specimen.isTb
    if (tbFilter === 'nonTb') return !specimen.isTb

    return true
  })

  // const onTogglePreventiveTreatment = () =>
  //   setIsPreventiveTreatmentOpen((prev) => !prev)

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" align="center">
        <Text className={styles.text}>
          {t('persons.person_investigations')}
        </Text>

        <Flex gap={8} align="center">
          <Segmented<'all' | 'tb' | 'nonTb'>
            value={tbFilter}
            onChange={setTbFilter}
            options={[
              { label: t('common.all'), value: 'all' },
              { label: t('organizational_structure.lab'), value: 'nonTb' },
              { label: t('consultations.tb_investigations'), value: 'tb' }
            ]}
          />
          <Button type="primary" onClick={onToggle}>
            <PlusOutlined />
            {t(
              isScreening
                ? 'screening.add_screening'
                : 'persons.investigations.investigation_new'
            )}
          </Button>

          <Button type="primary" onClick={onToggleTb}>
            <PlusOutlined />
            {t('consultations.new_tb_investigation_request')}
          </Button>

          <Button type="primary" onClick={onToggleExternal}>
            <PlusOutlined />
            {t('persons.external_result.add')}
          </Button>
        </Flex>
      </Flex>

      <Table
        bordered
        dataSource={filteredSpecimens}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      {/* <PreventiveTreatmentTable /> */}

      {isOpen && (canAddForeignInvestigation || isScreening) && (
        <InvestigationDemandModalScreening
          onSuccess={onSuccessCreate}
          onCancel={onToggle}
        />
      )}

      {isOpen && !canAddForeignInvestigation && !isScreening && (
        <InvestigationDemandModal
          onSuccess={onSuccessCreate}
          onCancel={onToggle}
        />
      )}

      {isTbOpen && (
        <InvestigationDemandModal
          isTb
          onSuccess={onSuccessCreate}
          onCancel={onToggleTb}
        />
      )}

      {isExternalOpen && (
        <ExternalResultModal
          personId={String(id)}
          onSuccess={onSuccessCreate}
          onCancel={onToggleExternal}
        />
      )}

      {/* {isPreventiveTreatmentOpen && (
        <PreventiveTreatmentModal
          onCancel={() => setIsPreventiveTreatmentOpen(false)}
        />
      )} */}
    </Flex>
  )
}
